/**
 * Store Front Types
 * Type definitions for the store-front Redux state
 */

export enum SupportedCurrency {
    USD = 'USD',
    CAD = 'CAD',
    GBP = 'GBP',
    EUR = 'EUR',
    CHF = 'CHF',
}

export interface AuthState {
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    sessionId: string | null;
    sessionToken: string | null;
}

export interface UserState {
    balance: number;
    balanceCurrency: SupportedCurrency;
    email: string;
    firstName: string;
    lastName: string;
    validEmail: boolean;
    zipCode: string;
    phoneNumber: string;
}

export interface PartnerDisabledData {
    message: string;
    brandImage?: string;
    moreInfoMessage?: string;
    moreInfoLink?: string;
    contact?: string;
}

export interface PartnerState {
    partnerId: string;
    prodAllowed: boolean;
    isLive: boolean;
    isLoading: boolean;
    error: string | null;
    disabledData: PartnerDisabledData | null;
}

export interface UIState {
    showUserDetails: boolean;
    isWidgetLoaded: boolean;
    showResetPassword: boolean;
    isAnimating: boolean;
}

export interface AuctionParams {
    partner_id?: string;
    session_id?: string;
    miniWidget?: boolean;
    user?: {
        balance: number;
        balance_currency: string;
        email: string;
        firstname: string;
        lastname: string;
    };
}

export interface SDKSettings {
    // These may come at top level for callback_params security type
    partner_id?: string;
    session_id?: string;
    // User object from server - contains session_id inside
    user?: {
        firstname: string;
        lastname?: string;
        year_of_birth: number;
        gender?: string;
        email: string;
        user_id: string;
        session_id: string;
        country: string;
        region: string;
        balance: number;
        [key: string]: unknown;
    };
    // Publisher object from server - contains id (partner_id) and apikey
    publisher?: {
        name: string;
        logo?: string;
        id: string; // This is the partner_id
        apikey: string;
        allowDestEmailChange?: boolean;
        balanceCopy?: string;
        [key: string]: unknown;
    };
}

export interface LoginCredentials {
    accessToken: string;
    pin: string;
}

export interface SessionData {
    session_id: string;
    session_token: string;
}

export interface UserDetailsFormData {
    firstName: string;
    lastName: string;
    email: string;
    zipCode: string;
    phoneNumber: string;
    acceptTerms: boolean;
}

export interface LoginResponse {
    session_id: string;
    session_token: string;
    balance: number;
    partner_currency?: string;
    email?: string;
    firstname?: string;
    lastname?: string;
    partner?: PartnerLiveResponse;
    email_status?: {
        valid_email: boolean;
        show_storefront_user_details: boolean;
    };
    error?: boolean;
    message?: string;
}

export interface AuthResponse {
    user: Record<string, unknown>;
    wallet_balance: number;
    error?: string;
}

export interface PartnerLiveResponse {
    prod_allowed: boolean;
    message?: string;
    brand_image?: string;
    more_info_message?: string;
    more_info_link?: string;
    contact?: string;
}

export interface ValidEmailResponse {
    valid_email: boolean;
    show_storefront_user_details?: boolean;
}
